/**
 * Which hardware SKUs the configurator can put on a non-CCI BOM, and where
 * each one stands (SP4).
 *
 * ENUMERATED, not described. The offered set is produced by asking the REAL
 * part-number resolvers what each selectable option resolves to, so quirks in
 * those resolvers — the CONF-45 override that sends directors to an NDR200 NIC
 * under both interface options, while storage nodes still reach the 200GBE OCP
 * card under Ethernet — are reported correctly by anyone who has never heard of
 * CONF-45. A second, declarative model of the same thing would get that wrong
 * in whichever direction its author guessed.
 *
 * This module is the seam. Today it reads vendorRegistry and productDb; when
 * products move to the cloud catalog, its internals change and its callers do
 * not.
 */
import {
  getActiveNodePartNumbers,
  getActiveStorageDrivePartNumbers,
  getActiveNetworkPartNumbers,
} from '../utils/partNumberUtils';
import { getVendor } from '../config/vendorRegistry';
import { getProductPrice } from '../utils/configUtilities';
import { snapshotFromSeed } from '../pricing/seedBuilder';
import { priceSku } from '../pricing/engine';
import { notDecomposedProductSkus } from '../admin/catalog/categories';
import { familyOf } from './productFamilies';

/** Quarters are 'YYYY-QN' and sort correctly as strings within a century. */
const notYet = (availableFrom, quarter) => !!availableFrom && availableFrom > quarter;

/**
 * THE "is this SKU priced this quarter" predicate for this module, in integer
 * cents. ONE implementation, used by both the priced-SKU filter and the row
 * that reports the number, so the two can never disagree about whether a SKU
 * has a price.
 *
 * Rounds BEFORE comparing, deliberately: `seedBuilder.storedPriceCents`
 * (:64-78) is the canonical form of this predicate and its comment explains
 * why — "a stored price in (0, 0.005) dollars is unpriced — the same verdict
 * the terminator pass and the remainder solver must reach". Comparing the
 * dollars first would make a $0.004 SKU `unpriced` to the publish-coverage
 * machinery and `offerable at $0.00` here, which is the disagreement this
 * view exists to surface rather than to create.
 *
 * NOT the canonical helper itself, and that is a choice with a reason: it
 * resolves the pricing key by computing it (`pricingQ32026` from any quarter),
 * while `getProductPrice` looks it up in `quarterConfig.quarterMapping`, which
 * only covers 2026-Q1..2028-Q4. Importing it would silently start pricing the
 * 2025 quarters productDb still carries, which is a scope change, not a
 * rounding fix. The two agree exactly across every sellable quarter; when
 * productDb goes away this whole reader goes with it and the question dies.
 */
const storedCents = (productDb, quarter, sku) => {
  const cents = Math.round((getProductPrice(productDb, quarter, sku) || 0) * 100);
  return cents > 0 ? cents : null;
};

/** Walk a resolver's nested return value and collect every part number. */
function collect(shape, into) {
  for (const value of Object.values(shape ?? {})) {
    if (typeof value === 'string' && value.startsWith('VCH-')) into.add(value);
    else if (value && typeof value === 'object') {
      if (typeof value.partNumber === 'string') into.add(value.partNumber);
      else collect(value, into);
    }
  }
}

/**
 * What governs a SKU is WHICH RESOLVER produced it, and that is known for free
 * because the three are asked separately. Nodes and NICs come from the
 * part-number constants; only the drive slots are governed by the vendor's
 * capabilities.
 *
 * The storage resolver LABELS its own slots by role, so reading the label is
 * asking it, not re-describing it — `collect()` flattens exactly the
 * information attribution needs, which is why the drive slots are read
 * directly. `productOffering.test.js` pins every slot path; if a resolver
 * renames one, the governedBy assertions fail rather than degrading silently.
 */
const NODE_GOVERNOR = 'PART_NUMBERS.NODES';
const NETWORK_GOVERNOR = 'PART_NUMBERS.NETWORK';

const SLOTS = {
  velo:      (drives) => drives.director?.ssdPartNumber,
  hybridSsd: (drives) => drives.hybrid?.ssdPartNumber,
  afaSsd:    (drives) => drives.afa?.ssdPartNumber,
  hybridHdd: (drives) => drives.hybrid?.hddPartNumber,
};

/** The entry a dimension falls back to when it is not the one being varied:
 *  the first one actually offered this quarter, else the first at all.
 *
 *  The quarter check is not cosmetic. The backdrop's part number is planted in
 *  every config, so a future-dated backdrop would hand a SKU a `rec` in a
 *  quarter where it should have none — and `unpriced` outranks `future`, so a
 *  SKU not yet in the price book would be reported as the fabrication alarm
 *  instead of as merely not-yet-available. The final `?? [0]` is about
 *  resolvability alone: a config must resolve even for a vendor offering
 *  nothing this quarter. */
const backdropEntry = (list, quarter) =>
  (list ?? []).find((e) => e.enabled !== false && !notYet(e.availableFrom, quarter))
  ?? (list ?? [])[0];

/**
 * The baseline every enumerated config starts from. Counts are 1 rather than 0
 * because a resolver skips a node class whose flag is off — the counts
 * themselves never reach the SKU set.
 *
 * Every drive field is populated, and that is load-bearing rather than tidy:
 * `getActiveStorageDrivePartNumbers` resolves the director SSD unconditionally
 * and both storage SSDs whenever their node flags are on, and it reaches them
 * through `generateSsdPartNumber`, which calls `.toString()` on the capacity.
 * A baseline that leaves those fields undefined therefore THROWS on every
 * config not varying that dimension, rather than quietly resolving nothing.
 * Values come from the vendor's own capabilities, so this module hard-codes no
 * product data.
 */
function baselineFor(caps, quarter) {
  const mappings = caps.productMappings ?? {};
  return {
    veloCount: 1, hybridNodeCount: 1, afaNodeCount: 1,
    hybridEnabled: true, afaEnabled: true,
    interfaceOption: caps.networkOptions?.[0]?.id ?? '100GbE',
    veloSsdCapacity: caps.veloSsdCapacities?.[0],
    hybridSsdPartNumber: backdropEntry(mappings.hybridSsd, quarter)?.partNumber,
    afaSsdPartNumber: backdropEntry(mappings.afaSsd, quarter)?.partNumber,
    // No JBOD in the backdrop: the resolver skips the HDD entirely at 0, which
    // keeps JBOD SKUs attributable to the JBOD dimension alone.
    hybridHddCapacity: 0,
  };
}

/**
 * One entry per selectable option, each with the config fragment that selects
 * it and a human-readable label. Dimension-wise, not a cross product: the
 * resolvers are independent per dimension, so varying one at a time against
 * the baseline yields the same union for ~40 configs instead of ~5,000. The
 * exception is network, which depends on interface AND node class, so those
 * vary together.
 */
function enumerateSelections(caps) {
  const out = [];
  const mappings = caps.productMappings ?? {};

  for (const capacity of caps.veloSsdCapacities ?? []) {
    out.push({ config: { veloSsdCapacity: capacity },
               reach: `Director node · ${capacity}TB NVMe`,
               governedBy: 'capabilities.veloSsdCapacities',
               slot: 'velo' });
  }

  for (const [key, label] of [['hybridSsd', 'Hybrid'], ['afaSsd', 'AFA']]) {
    for (const entry of mappings[key] ?? []) {
      if (entry.enabled === false) continue;
      const configKey = key === 'afaSsd' ? 'afaSsdPartNumber' : 'hybridSsdPartNumber';
      out.push({ config: { [configKey]: entry.partNumber },
                 reach: `${label} · ${entry.displayName ?? `${entry.capacity}TB`}`,
                 governedBy: `productMappings.${key}`,
                 slot: key,
                 entry });
    }
  }

  for (const size of caps.availableJbodSizes ?? []) {
    for (const entry of mappings.hybridHdd?.[size] ?? []) {
      if (entry.enabled === false) continue;
      out.push({ config: { jbodSize: size, hybridHddPartNumber: entry.partNumber,
                           hybridHddCapacity: entry.capacity },
                 reach: `Hybrid · ${size}-bay JBOD · ${entry.displayName ?? `${entry.capacity}TB`}`,
                 governedBy: `productMappings.hybridHdd[${size}]`,
                 slot: 'hybridHdd',
                 entry });
    }
  }

  // `slot: null` marks the node/network dimension: what varies here is which
  // node classes and which fabric land on the BOM, so this is the one
  // dimension that speaks for nodes and NICs. Its governor is not fixed per
  // selection — each of the two resolvers names its own.
  for (const option of caps.networkOptions ?? []) {
    for (const [hybridEnabled, afaEnabled, who] of [
      [true, false, 'Hybrid'], [false, true, 'AFA'], [false, false, 'Director only'],
    ]) {
      out.push({ config: { interfaceOption: option.id, hybridEnabled, afaEnabled },
                 reach: `${who} · ${option.id}`,
                 slot: null });
    }
  }

  // CONF-210: the Dual CPU flash node is a node-class choice of its own. One
  // selection is enough — it books a different node SKU, and its extra PCIe
  // card is the same SKU the plain AFA selection already reaches. Only
  // vendors that offer the node enumerate it, so its SKU reads `unreachable`
  // elsewhere (and everywhere once the kill switch is off — see
  // src/utils/dualCpuFlashFeature.js).
  if (caps.supportsDualCpuFlash) {
    out.push({ config: { hybridEnabled: false, afaEnabled: true, afaNodeModel: 'dualCpu' },
               reach: 'AFA · Dual CPU node',
               slot: null });
  }

  return out;
}

export function resolveOfferedProducts(vendorId, quarter, { productDb, seed } = {}) {
  const vendor = getVendor(vendorId);
  const caps = vendor.capabilities;
  const families = caps.productFamilies;

  // sku -> { reachedBy: Set, governedBy: Set }
  const reached = new Map();
  const note = (sku, reach, governor) => {
    if (!reached.has(sku)) reached.set(sku, { reachedBy: new Set(), governedBy: new Set() });
    const rec = reached.get(sku);
    rec.reachedBy.add(reach);
    rec.governedBy.add(governor);
  };

  // Attribution is per DIMENSION, not per config. Every config carries a full
  // backdrop — nodes, NICs and a drive in each slot — so crediting a selection
  // with everything its BOM happens to contain would claim, falsely, that a
  // node SKU is governed by `productMappings.hybridHdd[78]`. A selection speaks
  // only for what it selects: a drive dimension for its own labelled slot, the
  // node/network dimension for the nodes and NICs it switches on and off.
  // hardwareVendor rides in the backdrop because the flash node resolver
  // (src/utils/flashNodeModel.js) applies vendor eligibility itself.
  const baseConfig = { ...baselineFor(caps, quarter), hardwareVendor: vendorId };
  const selections = enumerateSelections(caps);
  for (const selection of selections) {
    if (notYet(selection.entry?.availableFrom, quarter)) continue;
    const config = { ...baseConfig, ...selection.config };

    if (selection.slot) {
      const drives = getActiveStorageDrivePartNumbers(config, productDb, vendorId);
      const sku = SLOTS[selection.slot](drives);
      if (sku) note(sku, selection.reach, selection.governedBy);
      continue;
    }

    const nodes = new Set();
    collect(getActiveNodePartNumbers(config), nodes);
    for (const sku of nodes) note(sku, selection.reach, NODE_GOVERNOR);

    const nics = new Set();
    collect(getActiveNetworkPartNumbers(config, caps), nics);
    for (const sku of nics) note(sku, selection.reach, NETWORK_GOVERNOR);
  }

  // Withheld and future entries are not enumerated, but must still be listed —
  // a SKU absent for a reason is exactly what this view exists to show.
  const withheld = new Map();
  for (const selection of selections.filter((s) => s.entry)) {
    if (notYet(selection.entry.availableFrom, quarter)) {
      withheld.set(selection.entry.partNumber,
                   { status: 'future', availableFrom: selection.entry.availableFrom, selection });
    }
  }
  for (const [key, entries] of Object.entries(caps.productMappings ?? {})) {
    const flat = Array.isArray(entries) ? entries.map((e) => [key, e])
      : Object.entries(entries).flatMap(([size, list]) =>
        list.map((e) => [`${key}[${size}]`, e]));
    for (const [where, entry] of flat) {
      if (entry.enabled === false && !withheld.has(entry.partNumber)) {
        withheld.set(entry.partNumber, {
          status: 'hidden',
          selection: { reach: null, governedBy: `productMappings.${where}` },
        });
      }
    }
  }

  const products = productDb?.products ?? {};
  const priced = Object.keys(products).filter(
    (sku) => sku.startsWith('VCH-') && storedCents(productDb, quarter, sku) !== null,
  );

  const snapshot = seed ? snapshotFromSeed(seed, quarter) : null;
  const lumps = notDecomposedProductSkus(seed);

  const skus = [...new Set([...reached.keys(), ...withheld.keys(), ...priced])].sort();

  return skus.map((sku) => {
    const product = products[sku];
    const storedPriceCents = storedCents(productDb, quarter, sku);
    const held = withheld.get(sku);
    const rec = reached.get(sku);

    let status;
    if (product?.isActive === false) status = 'retired';
    else if (rec && storedPriceCents === null) status = 'unpriced';
    else if (held) status = held.status;
    else if (!rec) status = 'unreachable';
    else status = 'offerable';

    // No composition at all is NOT "decomposed" — `notDecomposedProductSkus`
    // only walks the compositions that exist, so an unauthored SKU is absent
    // from its Set and `!lumps.has(sku)` would claim, of a SKU whose cost we do
    // not know at all, that we know its cost basis. Both facts come from the
    // one snapshot: no composition means null cost and not decomposed.
    const composed = !!snapshot?.compositions?.[sku];
    const costMicros = composed ? priceSku(sku, snapshot).rollupMicros : null;
    const decomposed = composed && !lumps.has(sku);

    return {
      sku,
      family: familyOf(sku, families),
      status,
      // Gated on the COMPUTED status, not merely on the entry carrying a date:
      // `retired` and `unpriced` both outrank `future`, so a retired SKU whose
      // mapping entry is also future-dated would otherwise be reported as
      // "retired (2026-Q4)" — a date attached to a status it does not qualify.
      // The contract says availableFrom is set only when status is `future`.
      ...(status === 'future' && held?.availableFrom ? { availableFrom: held.availableFrom } : {}),
      reachedBy: rec ? [...rec.reachedBy].sort() : [],
      governedBy: [...(rec?.governedBy ?? []), ...(held ? [held.selection.governedBy] : [])]
        .filter(Boolean).sort().join(', ') || 'nothing — priced but unreachable',
      storedPriceCents,
      costMicros,
      decomposed,
    };
  });
}
